import {
  Box,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect } from "react";
import Grid2 from "@mui/material/Grid2";
import TextInput from "../../components/Input/TextInput";
import CountrySelect from "../../components/SelectComponent/SelectCountry";
import { MuiPhoneComponent } from "../../components/Input/MuiPhoneComponent";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { Form } from "formik";
import TextAreaComponent from "../../components/Input/TextAreaComponent";
import Checkbox from "@mui/material/Checkbox";
import DatePickerComponent from "../../components/SelectComponent/DatePicker";
import TimePickerComponent from "../../components/SelectComponent/TimePickerComponent";
import CheckboxListComponent from "../../components/SelectComponent/CheckBoxListComponent";
import RadioComponent from "../../components/SelectComponent/RadioComponent";
import NumberInputComponent from "../../components/Input/NumberInput";
import CardSection from "./CardSectionSalon";
import { setAddFieldsData } from "../../store/slice/Salon.slice";

// ─── Reusable label with optional required asterisk ──────────────────────────
const RequiredStar = () => (
  <Box
    component="span"
    sx={{
      color: "error.main",
      ml: 0.375,
      fontSize: "inherit",
      lineHeight: "inherit",
      verticalAlign: "middle",
    }}
    aria-hidden="true"
  >
    *
  </Box>
);

const FieldLabel = ({ children, required, component: Component = InputLabel, sx = {}, htmlFor }) => (
  <Component htmlFor={htmlFor} sx={sx}>
    {children}
    {required && <RequiredStar />}
  </Component>
);


const UserDetailsSection = ({
  steps,
  activeStep,
  handleBack,
  handleNextFunc,
  price,
  isLoading,
  values,
  errors,
  touched,
  setField,
  GuestDetailsData,
  setGuestFields,
  FieldsData,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const { widgetConfig, guestInfo, defaultLanguage, css } = useSelector(
    (store) => store?.widgetSettings
  );
  const Css = css?.find(
    (item) => item?.cssTabFieldsGroupName === "Contact details"
  )?.fields;

  const { languageWords } = useSelector((store) => store?.salon);

  useEffect(() => {
    const guestFields = GuestDetailsData?.map((field) => ({
      idfield: field?.tag,
      data: values[field?.name],
    }));

    const updatedFields = FieldsData?.map((field) => {
      let fieldValue = values?.[field?.name] ?? "";
      if (field?.type === "time" && fieldValue) {
        fieldValue = dayjs(fieldValue).format("HH:mm:ss");
      }
      return { ...field, data: fieldValue };
    });

    dispatch(setAddFieldsData(updatedFields));
    dispatch(setGuestFields(guestFields));
  }, [values]);

  // ── Shared label sx helpers ──────────────────────────────────────────────
  const labelSx = {
    fontWeight: "bold",
    fontSize: { xs: "0.8125rem", sm: "0.9375rem" },
    color: theme?.details?.formLabelColor,
    whiteSpace: "normal",
    wordBreak: "break-word",
    flexShrink: 0,
  };

  const inlineLabelSx = {
    ...labelSx,
    mt: { xs: 0, md: 1 },
    minWidth: { md: 150 },
  };

  return (
    <Form>
      <Box sx={{ mt: 5, p: { xs: 1.5, sm: 2 } }}>
        <Grid2 container spacing={2}>

          {/* ── Left column: contact + extra fields ── */}
          <Grid2 size={{ md: 9, sm: 12, xs: 12 }}>

            {/* Section heading */}
            <Typography
              variant="h6"
              sx={{
                color: Css?.contactdetailsHeadingClr,
                fontSize: { xs: "1.0625rem", sm: "1.25rem" },
                fontWeight: "bold",
                mt: 1,
                mb: 1.5,
              }}
            >
              {languageWords?.ContactData}
            </Typography>

            {/* ── Read-only mode (token/logged-in guest) ── */}
            {widgetConfig?.token ? (
              <Box sx={{ mt: 2 }}>
                {[
                  { key: "salutation", label: languageWords?.Salutation || "Salutation" },
                  { key: "firstname",  label: languageWords?.firstname  || "First Name" },
                  { key: "lastname",   label: languageWords?.lastname   || "Last Name" },
                  { key: "email",      label: languageWords?.email      || "Email" },
                  { key: "address",    label: languageWords?.address    || "Address" },
                  { key: "country",    label: languageWords?.country    || "Country" },
                  { key: "zipcode",    label: languageWords?.zipcode    || "Zipcode" },
                  { key: "phone",      label: languageWords?.phone      || "Phone" },
                  { key: "place",      label: languageWords?.place      || "Place" },
                  { key: "otherfields",label: languageWords?.otherfields|| "Other fields" },
                ]
                  .filter(({ key }) => guestInfo?.[key])
                  .map(({ key, label }) => (
                    <Grid2 container key={key} sx={{ mb: 0.5 }}>
                      <Grid2 size={{ xs: 6, md: 2 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                            color: Css?.labelColor,
                          }}
                        >
                          {label}
                        </Typography>
                      </Grid2>
                      <Grid2 size={{ xs: 6, md: 3 }}>
                        <Typography
                          sx={{
                            fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                            color: Css?.labelColor,
                          }}
                        >
                          {guestInfo?.[key]}
                        </Typography>
                      </Grid2>
                    </Grid2>
                  ))}
              </Box>
            ) : (
              /* ── Editable guest fields ── */
              <Grid2 container spacing={2}>
                {GuestDetailsData?.map((field, i) => {
                  const fieldName = field?.name;
                  const isRequired = Boolean(field?.required || field?.mandatory);

                  switch (field?.tag) {

                    case "{land}":
                      return (
                        <React.Fragment key={i}>
                          <Grid2 size={{ md: 3, xs: 12 }}>
                            <FieldLabel
                              required={isRequired}
                              sx={{ ...labelSx, mb: 2 }}
                            >
                              {fieldName}
                            </FieldLabel>
                          </Grid2>
                          <Grid2 size={{ xs: 12, md: 3 }}>
                            <CountrySelect
                              style={{ width: "100%" }}
                              label={fieldName}
                              name={fieldName}
                              value={values[fieldName]}
                              onChange={setField}
                            />
                          </Grid2>
                        </React.Fragment>
                      );

                    case "date":
                      return (
                        <React.Fragment key={i}>
                          <Grid2 size={{ md: 3, xs: 12 }}>
                            <FieldLabel
                              required={isRequired}
                              htmlFor={fieldName}
                              sx={{ ...labelSx, mb: 2, mt: 1 }}
                            >
                              {fieldName}
                            </FieldLabel>
                          </Grid2>
                          <Grid2 size={{ xs: 12, md: 3 }}>
                            <DatePickerComponent
                              label={field?.info}
                              name={fieldName}
                            />
                          </Grid2>
                        </React.Fragment>
                      );

                    case "{email}":
                      return (
                        <React.Fragment key={i}>
                          <Grid2 size={{ md: 3, sm: 12 }}>
                            <FieldLabel
                              required={isRequired}
                              htmlFor={fieldName}
                              sx={inlineLabelSx}
                            >
                              {fieldName}
                            </FieldLabel>
                          </Grid2>
                          <Grid2 size={{ xs: 12, md: 3 }}>
                            <TextInput
                              showLabel
                              label={fieldName}
                              name={fieldName}
                              value={values[fieldName]}
                              onChange={setField}
                              error={errors[fieldName]}
                              helperText={touched[fieldName] && errors[fieldName]}
                              borderClr={Css?.fieldBorderClr}
                              errorBorderClr={Css?.errorBorder}
                            />
                          </Grid2>
                        </React.Fragment>
                      );

                    case "{phone}":
                      return (
                        <React.Fragment key={i}>
                          <Grid2 size={{ md: 3, xs: 12 }}>
                            <FieldLabel
                              required={isRequired}
                              component={Typography}
                              sx={{
                                ...labelSx,
                                mb: { xs: 1, md: 0 },
                                mr: { md: 1 },
                                minWidth: { md: 150 },
                                whiteSpace: "nowrap",
                              }}
                            >
                              {fieldName}
                            </FieldLabel>
                          </Grid2>
                          <Grid2 size={{ xs: 12, md: 3 }}>
                            <MuiPhoneComponent
                              showLabel
                              label={fieldName}
                              name={fieldName}
                              value={values[fieldName]}
                              onChange={setField}
                              error={errors[fieldName]}
                              helperText={touched[fieldName] && errors[fieldName]}
                            />
                          </Grid2>
                        </React.Fragment>
                      );

                    default:
                      return (
                        <React.Fragment key={i}>
                          <Grid2 size={{ md: 3, sm: 12 }}>
                            <FieldLabel
                              required={isRequired}
                              htmlFor={fieldName}
                              sx={inlineLabelSx}
                            >
                              {fieldName}
                            </FieldLabel>
                          </Grid2>
                          <Grid2 size={{ xs: 12, md: 3 }}>
                            <TextInput
                              showLabel
                              label={fieldName}
                              name={fieldName}
                              value={values[fieldName]}
                              onChange={setField}
                              error={errors[fieldName]}
                              helperText={touched[fieldName] && errors[fieldName]}
                            />
                          </Grid2>
                        </React.Fragment>
                      );
                  }
                })}
              </Grid2>
            )}

            {/* ── Extra / additional fields ── */}
            {FieldsData?.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: theme?.details?.formHeaderColor,
                    fontSize: { xs: "1.0625rem", sm: "1.25rem" },
                    fontWeight: "bold",
                    mb: 2,
                  }}
                >
                  {languageWords?.ExtraInformation}
                </Typography>

                <Grid2 container spacing={2}>
                  {FieldsData?.map((field, i) => {
                    const fieldName =
                      field?.fieldsLanguages?.find(
                        (item) => item?.fieldname === "name"
                      )?.data ?? field?.name;

                    const fieldInfo =
                      field?.fieldsLanguages?.find(
                        (item) => item?.fieldname === "info"
                      )?.data ?? field?.info;

                    const isRequired = Boolean(field?.required || field?.mandatory);

                    switch (field?.type) {

                      case "textarea":
                        return (
                          <Grid2 container spacing={2} sx={{ width: "100%" }} key={i}>
                            {field?.showlabel && fieldName?.trim() && (
                              <Grid2 size={{ md: 4, xs: 12 }}>
                                <FieldLabel
                                  required={isRequired}
                                  sx={{
                                    fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                                    color: theme?.details?.formLabelColor,
                                    fontWeight: 600,
                                    mt: { xs: 1, md: 2 },
                                  }}
                                >
                                  {fieldName}
                                </FieldLabel>
                              </Grid2>
                            )}
                            <Grid2 size={{ md: field?.showlabel && fieldName?.trim() ? 8 : 12, xs: 12 }}>
                              <TextAreaComponent
                                label={fieldName}
                                name={fieldName?.replace(".", "")}
                                setField={setField}
                                value={values[fieldName]}
                                error={errors[fieldName]}
                              />
                            </Grid2>
                          </Grid2>
                        );

                      case "number":
                        return (
                          <Grid2 container sx={{ width: "100%" }} key={i}>
                            <Grid2 size={{ xs: 12, md: 4 }}>
                              <FieldLabel
                                required={isRequired}
                                htmlFor={fieldName}
                                sx={labelSx}
                              >
                                {fieldInfo}
                              </FieldLabel>
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 8 }}>
                              <NumberInputComponent
                                showLabel={field?.showlabel}
                                label={fieldInfo}
                                name={fieldName}
                                setField={setField}
                                value={values[fieldName] || 0}
                                error={errors[fieldName]}
                              />
                            </Grid2>
                          </Grid2>
                        );

                      case "date":
                        return (
                          <Grid2 container sx={{ width: "100%" }} key={i}>
                            <Grid2 size={{ xs: 12, md: 4 }}>
                              <FieldLabel
                                required={isRequired}
                                htmlFor={fieldName}
                                sx={{ ...labelSx, mb: 2, mt: 1 }}
                              >
                                {fieldName}
                              </FieldLabel>
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 8 }}>
                              <DatePickerComponent
                                showLabel={field?.showlabel}
                                label={fieldName}
                                name={fieldName}
                                value={values[fieldName]}
                                onChange={setField}
                                lang={defaultLanguage}
                                error={errors[fieldName]}
                              />
                            </Grid2>
                          </Grid2>
                        );

                      case "time":
                        return (
                          <Grid2 container key={i} sx={{ width: "100%" }}>
                            <Grid2 size={{ xs: 12, md: 4 }}>
                              {field?.showlabel && (
                                <FieldLabel
                                  required={isRequired}
                                  htmlFor={fieldName}
                                  sx={{ ...labelSx, mt: 1 }}
                                >
                                  {fieldName}
                                </FieldLabel>
                              )}
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 8 }}>
                              <TimePickerComponent
                                showLabel={field?.showlabel}
                                label={fieldName}
                                name={fieldName}
                                value={values[fieldName]}
                                onChange={setField}
                              />
                            </Grid2>
                          </Grid2>
                        );

                      case "tel":
                        return (
                          <Grid2 container sx={{ width: "100%" }} key={i}>
                            <Grid2 size={{ md: 4, xs: 12 }}>
                              <FieldLabel
                                required={isRequired}
                                component={Typography}
                                sx={{
                                  ...labelSx,
                                  mb: { xs: 1, md: 0 },
                                  mr: { md: 1 },
                                  minWidth: { md: 150 },
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {fieldName}
                              </FieldLabel>
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 8 }}>
                              <MuiPhoneComponent
                                showLabel={field?.showlabel}
                                label={fieldName}
                                name={fieldName}
                                value={values[fieldName]}
                                onChange={setField}
                                error={errors[fieldName]}
                                helperText={touched[fieldName] && errors[fieldName]}
                              />
                            </Grid2>
                          </Grid2>
                        );

                      case "radio":
                        return (
                          <Grid2 container sx={{ width: "100%" }} key={i}>
                            <Grid2 size={{ md: 4, xs: 12 }}>
                              {field?.showlabel && (
                                <FieldLabel
                                  required={isRequired}
                                  sx={labelSx}
                                >
                                  {field?.fieldsLanguages?.[0]?.data}
                                </FieldLabel>
                              )}
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 8 }}>
                              <RadioComponent
                                showLabel={field?.showlabel}
                                field={field}
                                setField={setField}
                                error={errors[fieldName]}
                              />
                            </Grid2>
                          </Grid2>
                        );

                      case "select":
                        return (
                          <Grid2 container sx={{ width: "100%" }} key={i}>
                            <Grid2 size={{ md: 4, sm: 12 }}>
                              {field?.showlabel && fieldName && (
                                <FieldLabel
                                  required={isRequired}
                                  sx={{ ...labelSx, mb: 1 }}
                                >
                                  {fieldName}
                                </FieldLabel>
                              )}
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 8 }}>
                              <FormControl fullWidth>
                                <Select
                                  native
                                  name={fieldName}
                                  value={values[fieldName] || ""}
                                  onChange={(e) =>
                                    setField(fieldName, e.target.value)
                                  }
                                >
                                  {field?.fieldsLanguages?.length > 0
                                    ? field?.fieldsLanguages[2]?.data
                                        ?.split("\n")
                                        ?.map((item) => (
                                          <option value={item} key={item}>
                                            {item?.replace("[C]", " ")}
                                          </option>
                                        ))
                                    : field?.options
                                        ?.split("\n")
                                        ?.map((item) => (
                                          <option value={item} key={item}>
                                            {item?.replace("[C]", " ")}
                                          </option>
                                        ))}
                                </Select>
                              </FormControl>
                            </Grid2>
                          </Grid2>
                        );

                      case "termsandcondition":
                        return (
                          <Grid2 size={{ xs: 12 }} key={i}>
                            <InputLabel sx={{ fontWeight: "bold", mb: 1 }}>
                              {fieldName}
                              {/* Terms checkbox is implicitly required by nature */}
                              {isRequired && <RequiredStar />}
                            </InputLabel>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={values[fieldName] || false}
                                  onChange={(e) =>
                                    setField(fieldName, e.target.checked)
                                  }
                                  sx={{
                                    "&.Mui-checked": {
                                      color: theme?.details?.formCheckBoxColor,
                                    },
                                  }}
                                />
                              }
                              label={
                                <a
                                  href={field?.options}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: theme?.details?.formLinkColor }}
                                >
                                  {fieldInfo}
                                </a>
                              }
                            />
                          </Grid2>
                        );

                      case "checkbox":
                        return (
                          <Grid2 size={{ xs: 12, md: 12 }} key={i}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  name={fieldName}
                                  checked={values[fieldName] || false}
                                  onChange={(e) =>
                                    setField(fieldName, e.target.checked)
                                  }
                                  sx={{
                                    "&.Mui-checked": {
                                      color: theme?.details?.formCheckBoxColor,
                                    },
                                  }}
                                />
                              }
                              label={
                                // Inline asterisk inside the checkbox label
                                <Box component="span">
                                  {fieldInfo}
                                  {isRequired && <RequiredStar />}
                                </Box>
                              }
                            />
                          </Grid2>
                        );

                      case "checkboxList":
                      case "file":
                        return (
                          <Grid2 container sx={{ width: "100%" }} key={i}>
                            <Grid2 size={{ md: 4, xs: 12 }}>
                              {field?.showlabel && (
                                <FieldLabel
                                  required={isRequired}
                                  sx={{ ...labelSx, mb: 1 }}
                                >
                                  {field?.fieldsLanguages?.[0]?.data}
                                </FieldLabel>
                              )}
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 8 }}>
                              <CheckboxListComponent
                                showLabel={field?.showlabel}
                                field={field}
                                setField={setField}
                              />
                            </Grid2>
                          </Grid2>
                        );

                      case "link":
                        return (
                          <Grid2 size={{ xs: 12 }} key={i}>
                            <a
                              href={field?.fieldsLanguages?.[0]?.data}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: theme?.details?.formLinkColor,
                                fontSize: "15px",
                                fontWeight: "bold",
                              }}
                            >
                              {
                                field?.fieldsLanguages?.find(
                                  (item) => item?.fieldname === "options"
                                )?.data
                              }
                            </a>
                          </Grid2>
                        );

                      case "label":
                        return (
                          <Grid2 size={{ xs: 12, md: 12 }} key={i}>
                            {field?.showlabel && (
                              <Typography
                                sx={{ fontWeight: "bold", mb: 1, color: Css?.labelColor }}
                              >
                                {fieldName}
                                {isRequired && <RequiredStar />}
                              </Typography>
                            )}
                            <Typography>{fieldInfo}</Typography>
                          </Grid2>
                        );

                      default:
                        return (
                          <Grid2 container sx={{ width: "100%" }} key={i}>
                            <Grid2 size={{ md: 4, sm: 12 }}>
                              {field?.showlabel && (
                                <FieldLabel
                                  required={isRequired}
                                  htmlFor={fieldName}
                                  sx={{
                                    ...inlineLabelSx,
                                    color: Css?.labelColor,
                                  }}
                                >
                                  {fieldName}
                                </FieldLabel>
                              )}
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 8 }}>
                              <TextInput
                                showLabel={field?.showlabel}
                                label={fieldName}
                                name={fieldName}
                                value={values[fieldName]}
                                placeholder={fieldInfo}
                                onChange={setField}
                                error={errors[fieldName]}
                                helperText={touched[fieldName] && errors[fieldName]}
                              />
                            </Grid2>
                          </Grid2>
                        );
                    }
                  })}
                </Grid2>
              </Box>
            )}
          </Grid2>

          {/* ── Right column: summary card ── */}
          <Grid2 size={{ md: 3, sm: 12, xs: 12 }}>
            <CardSection
              steps={steps}
              activeStep={activeStep}
              handleBack={handleBack}
              handleNextFunc={handleNextFunc}
              price={price}
              errors={errors}
              isLoading={isLoading}
              touched={touched}
              values={values}
            />
          </Grid2>
        </Grid2>
      </Box>
    </Form>
  );
};

export default UserDetailsSection;
